/**
 * DateRangePicker Component
 * Advanced date range filter with preset options and custom date selection.
 * Uses MiniCalendar for a fully styled, brand-consistent calendar.
 */
import { Calendar, Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSettingsStore } from "../../../stores/settingsStore";
import MiniCalendar from "./MiniCalendar";

interface DateRangePickerProps {
  dateRange: string;
  setDateRange: (range: string) => void;
  showDatePicker: boolean;
  setShowDatePicker: (show: boolean) => void;
  customStartDate: string;
  setCustomStartDate: (date: string) => void;
  customEndDate: string;
  setCustomEndDate: (date: string) => void;
  compareToPrevious: boolean;
  setCompareToPrevious: (compare: boolean) => void;
  datePickerRef: React.RefObject<HTMLDivElement | null>;
}

const PRESET_RANGES = ["today", "last7days", "last30days"];

/** Format YYYY-MM-DD -> "Feb 22, 2026" */
function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  if (!y || !m || !d) return dateStr;
  const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function DateRangePicker({
  dateRange,
  setDateRange,
  showDatePicker,
  setShowDatePicker,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  datePickerRef,
}: DateRangePickerProps) {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((state) => state.darkMode);
  const today = new Date().toISOString().split("T")[0]!;

  // Which date field the calendar is editing: "start" or "end"
  const [activeField, setActiveField] = useState<"start" | "end">("start");

  const getDateRangeLabel = (range: string): string => t(`reports.${range}`);

  const handleCalendarChange = (dateStr: string) => {
    if (activeField === "start") {
      setCustomStartDate(dateStr);
      setDateRange("custom");
      // Auto-switch to end field after picking start
      setActiveField("end");
    } else {
      setCustomEndDate(dateStr);
      setDateRange("custom");
    }
  };

  const calendarValue =
    activeField === "start" ? customStartDate : customEndDate;
  const calendarMinProps =
    activeField === "end" && customStartDate ? { min: customStartDate } : {};

  return (
    <div className="relative" ref={datePickerRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setShowDatePicker(!showDatePicker)}
        className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium focus:outline-none transition-all ${
          showDatePicker
            ? "border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/20"
            : ""
        } ${
          darkMode
            ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
        }`}
      >
        <Calendar className="w-4 h-4 text-[var(--brand-primary)]" />
        <span>
          {dateRange === "custom"
            ? customStartDate && customEndDate
              ? `${formatDate(customStartDate)}  ${formatDate(customEndDate)}`
              : customStartDate
                ? `${formatDate(customStartDate)}  ...`
                : getDateRangeLabel(dateRange)
            : getDateRangeLabel(dateRange)}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${showDatePicker ? "rotate-180" : ""}`}
        />
      </button>

      {/* Date Range Dropdown */}
      {showDatePicker && (
        <div
          className={`absolute right-0 top-full mt-2 w-80 rounded-2xl shadow-xl border z-50 overflow-hidden ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="p-4 space-y-4">
            {/* Preset Ranges */}
            <div>
              <h4
                className={`text-xs font-semibold uppercase mb-2 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {t("reports.dateRangeFilter")}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {PRESET_RANGES.map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => {
                      setDateRange(range);
                      if (range !== "custom") setShowDatePicker(false);
                    }}
                    className={`px-3 py-2 text-xs font-medium rounded-lg transition-all text-left relative ${
                      dateRange === range
                        ? "bg-[var(--brand-primary)] text-white shadow-md shadow-[var(--brand-primary)]/30"
                        : darkMode
                          ? "bg-gray-700 text-gray-300 hover:bg-[var(--brand-primary)]/20 hover:text-[var(--brand-primary)]"
                          : "bg-gray-50 text-gray-700 hover:bg-[var(--brand-primary)]/10 hover:text-[var(--brand-primary)]"
                    }`}
                  >
                    {getDateRangeLabel(range)}
                    {dateRange === range}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Date Range — styled date field selectors + inline calendar */}
            <div
              className={`rounded-xl border p-3 space-y-3 ${
                darkMode
                  ? "border-gray-700 bg-gray-900/30"
                  : "border-gray-100 bg-gray-50/60"
              }`}
            >
              <div className="flex items-center justify-between">
                <h4
                  className={`text-xs font-semibold uppercase ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {t("reports.customRange")}
                </h4>
                {/* Today shortcut */}
                {/* <button
                  type="button"
                  onClick={() => {
                    setCustomStartDate(today);
                    setCustomEndDate(today);
                    setDateRange("custom");
                  }}
                  className="text-[10px] font-medium px-2 py-0.5 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/20 transition-all"
                >
                  {t("reports.today")}
                </button> */}
              </div>

              {/* Start / End field selectors */}
              <div className="grid grid-cols-2 gap-2">
                {/* Start Date */}
                <button
                  type="button"
                  onClick={() => setActiveField("start")}
                  className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left transition-all ${
                    activeField === "start"
                      ? "border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/20 bg-[var(--brand-primary)]/5"
                      : darkMode
                        ? "border-gray-600 bg-gray-700 hover:border-gray-500"
                        : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`text-[10px] font-semibold uppercase mb-0.5 ${
                      activeField === "start"
                        ? "text-[var(--brand-primary)]"
                        : darkMode
                          ? "text-gray-400"
                          : "text-gray-400"
                    }`}
                  >
                    {t("reports.startDate")}
                  </span>
                  <span
                    className={`text-xs font-medium ${
                      customStartDate
                        ? darkMode
                          ? "text-gray-100"
                          : "text-gray-800"
                        : darkMode
                          ? "text-gray-500"
                          : "text-gray-400"
                    }`}
                  >
                    {customStartDate ? formatDate(customStartDate) : "Select"}
                  </span>
                </button>

                {/* End Date */}
                <button
                  type="button"
                  onClick={() => setActiveField("end")}
                  className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left transition-all ${
                    activeField === "end"
                      ? "border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/20 bg-[var(--brand-primary)]/5"
                      : darkMode
                        ? "border-gray-600 bg-gray-700 hover:border-gray-500"
                        : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`text-[10px] font-semibold uppercase mb-0.5 ${
                      activeField === "end"
                        ? "text-[var(--brand-primary)]"
                        : darkMode
                          ? "text-gray-400"
                          : "text-gray-400"
                    }`}
                  >
                    {t("reports.endDate")}
                  </span>
                  <span
                    className={`text-xs font-medium ${
                      customEndDate
                        ? darkMode
                          ? "text-gray-100"
                          : "text-gray-800"
                        : darkMode
                          ? "text-gray-500"
                          : "text-gray-400"
                    }`}
                  >
                    {customEndDate ? formatDate(customEndDate) : "Select"}
                  </span>
                </button>
              </div>

              {/* Inline MiniCalendar */}
              <MiniCalendar
                value={calendarValue}
                onChange={handleCalendarChange}
                max={today}
                {...calendarMinProps}
                rangeStart={customStartDate}
                rangeEnd={customEndDate}
                darkMode={darkMode}
              />
            </div>

            {/* Actions */}
            <div
              className={`flex items-center gap-2 pt-2 border-t ${
                darkMode ? "border-gray-700" : "border-gray-100"
              }`}
            >
              <button
                type="button"
                onClick={() => setShowDatePicker(false)}
                className={`flex-1 px-4 py-2 rounded-lg transition-all text-sm font-medium ${
                  darkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {t("reports.cancel")}
              </button>
              <button
                type="button"
                onClick={() => setShowDatePicker(false)}
                className="flex-1 px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg hover:opacity-90 transition-all text-sm font-medium flex items-center justify-center gap-2 shadow-md shadow-[var(--brand-primary)]/20"
              >
                <Check className="w-4 h-4" />
                {t("reports.applyFilter")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
